// Frontend integration for Stripe pricing and discounts using Firebase Functions
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';
import { DisplayPackage } from '../types/revenuecat';
import { formatPrice } from '../utils/currency';
import { t } from '../i18n';

// Initialize Firebase Functions
const functions = getFunctions(app, 'europe-west2');

/**
 * Stripe Price with discount information
 */
export interface StripePriceWithDiscount {
  id: string;
  productId: string;
  productName: string;
  amount: number; // Original amount in cents
  discountedAmount?: number; // Amount after discount in cents
  discountPercent?: number; // Discount percentage
  currency: string;
  interval?: 'month' | 'year' | 'week' | 'day';
  intervalCount?: number;
  trialPeriodDays?: number;
  metadata?: {
    months?: string; // Number of months (as string from Stripe metadata)
    [key: string]: any;
  };
  productMetadata?: {
    trial_period_days?: string;
    [key: string]: any;
  };
  couponApplied?: {
    id: string;
    name: string;
    percentOff?: number;
    amountOff?: number;
  };
}

/**
 * Fetch all Stripe prices from Firebase Functions
 */
export async function fetchStripePrices(): Promise<StripePriceWithDiscount[]> {
  try {
    const fetchPrices = httpsCallable(functions, 'fetchStripePrices');
    const result = await fetchPrices();
    const data = result.data as { success: boolean; prices: StripePriceWithDiscount[]; error?: string };

    if (!data.success) {
      throw new Error(data.error || t('errors.stripePricing.fetchPrices'));
    }

    return data.prices;
  } catch (error) {
    console.error('Error fetching Stripe prices:', error);
    throw error;
  }
}

/**
 * Validate a coupon code and get discount information using Firebase Functions
 */
export async function validateCouponCode(
  couponCode: string,
  priceId: string
): Promise<{
  valid: boolean;
  coupon?: {
    id: string;
    name: string;
    percentOff?: number;
    amountOff?: number;
    duration: string;
    durationInMonths?: number;
  };
  pricing?: {
    originalAmount: number;
    discountAmount: number;
    discountedAmount: number;
    currency: string;
  };
  error?: string;
}> {
  try {
    const validateCoupon = httpsCallable(functions, 'validateStripeCoupon');
    const result = await validateCoupon({ couponCode, priceId });
    const data = result.data as any;

    if (!data.valid) {
      return {
        valid: false,
        error: data.error || t('errors.stripePricing.invalidCoupon'),
      };
    }

    return {
      valid: true,
      coupon: data.coupon,
      pricing: data.pricing,
    };
  } catch (error: any) {
    console.error('Error validating coupon:', error);
    return {
      valid: false,
      error: error.message || t('errors.stripePricing.validateCoupon'),
    };
  }
}

/**
 * Get active promotion codes from Firebase Functions
 */
export async function fetchPromotionCodes(): Promise<any[]> {
  try {
    const getPromoCodes = httpsCallable(functions, 'getStripePromotionCodes');
    const result = await getPromoCodes();
    const data = result.data as { success: boolean; promotionCodes: any[]; error?: string };

    if (!data.success) {
      throw new Error(data.error || t('errors.stripePricing.fetchPromoCodes'));
    }

    return data.promotionCodes;
  } catch (error) {
    console.error('Error fetching promotion codes:', error);
    throw error;
  }
}

/**
 * Create a subscription with optional discount using Firebase Functions
 */
export async function getOrCreateStripeCustomer(
  email: string,
  userId: string
): Promise<{
  success: boolean;
  customerId?: string;
  error?: string;
}> {
  try {
    const getCustomer = httpsCallable(functions, 'getOrCreateStripeCustomer');
    const result = await getCustomer({ email, userId });
    const data = result.data as any;

    if (!data.success) {
      return {
        success: false,
        error: data.error || t('errors.stripePricing.getCustomer'),
      };
    }

    return {
      success: true,
      customerId: data.customerId,
    };
  } catch (error: any) {
    console.error('Error getting or creating customer:', error);
    return {
      success: false,
      error: error.message || t('errors.stripePricing.getCustomer'),
    };
  }
}

export async function createStripeSubscription(
  customerId: string,
  priceId: string,
  paymentMethodId: string,
  couponCode?: string,
  trialPeriodDays?: number
): Promise<{
  success: boolean;
  subscriptionId?: string;
  clientSecret?: string;
  status?: string;
  error?: string;
}> {
  try {
    const createSubscription = httpsCallable(functions, 'createStripeSubscription');
    const result = await createSubscription({
      customerId,
      priceId,
      paymentMethodId,
      couponCode,
      trialPeriodDays,
    });
    const data = result.data as any;

    if (!data.success) {
      return {
        success: false,
        error: data.error || t('errors.stripePricing.createSubscription'),
      };
    }

    return {
      success: true,
      subscriptionId: data.subscriptionId,
      clientSecret: data.clientSecret,
      status: data.status,
    };
  } catch (error: any) {
    console.error('Error creating subscription:', error);
    return {
      success: false,
      error: error.message || t('errors.stripePricing.createSubscription'),
    };
  }
}

/**
 * Get or retrieve active subscription for a user
 */
export async function getOrCreateSubscription(
  userId: string
): Promise<{
  success: boolean;
  subscriptionId?: string;
  customerId?: string;
  status?: string;
  error?: string;
}> {
  try {
    const getSubscription = httpsCallable(functions, 'getOrCreateSubscription');
    const result = await getSubscription({ userId });
    const data = result.data as any;

    if (!data.success) {
      return {
        success: false,
        error: data.error || t('errors.stripePricing.getSubscription'),
      };
    }

    return {
      success: true,
      subscriptionId: data.subscriptionId,
      customerId: data.customerId,
      status: data.status,
    };
  } catch (error: any) {
    console.error('Error getting subscription:', error);
    return {
      success: false,
      error: error.message || t('errors.stripePricing.getSubscription'),
    };
  }
}

/**
 * Format price with discount for display
 */
export function formatPriceWithDiscount(price: StripePriceWithDiscount): {
  originalPrice: string;
  discountedPrice?: string;
  discountLabel?: string;
  savings?: string;
} {
  const originalPrice = formatPrice(price.amount, price.currency);

  if (!price.discountedAmount && !price.couponApplied) {
    return { originalPrice };
  }

  const discountedPrice = price.discountedAmount
    ? formatPrice(price.discountedAmount, price.currency)
    : undefined;

  let discountLabel: string | undefined;
  if (price.couponApplied?.percentOff) {
    discountLabel = t('stripePricing.percentOff', { percent: price.couponApplied.percentOff });
  } else if (price.couponApplied?.amountOff) {
    discountLabel = t('stripePricing.amountOff', { amount: formatPrice(price.couponApplied.amountOff, price.currency) });
  }

  const savings = price.discountedAmount
    ? t('stripePricing.savings', { amount: formatPrice(price.amount - price.discountedAmount, price.currency) })
    : undefined;

  return {
    originalPrice,
    discountedPrice,
    discountLabel,
    savings,
  };
}

/**
 * Merge Stripe prices with RevenueCat packages
 * This allows you to use Stripe pricing while maintaining RevenueCat for app store subscriptions
 */
export function mergeStripePricesWithPackages(
  packages: DisplayPackage[],
  stripePrices: StripePriceWithDiscount[]
): DisplayPackage[] {
  return packages.map(pkg => {
    // Match by store_identifier (should match Stripe price ID)
    const stripePrice = stripePrices.find(p => p.id === pkg.product.store_identifier);

    if (stripePrice) {
      return {
        ...pkg,
        product: {
          ...pkg.product,
          price: stripePrice.discountedAmount || stripePrice.amount,
          price_string: formatPrice(stripePrice.discountedAmount || stripePrice.amount, stripePrice.currency),
          // Add discount info to metadata
          discount_info: stripePrice.couponApplied
            ? {
                original_price: stripePrice.amount,
                discount_percent: stripePrice.couponApplied.percentOff,
                discount_amount: stripePrice.couponApplied.amountOff,
                coupon_name: stripePrice.couponApplied.name,
              }
            : undefined,
        },
      };
    }

    return pkg;
  });
}
